import { useEffect, useMemo, useRef, useState } from 'react';

interface PixelGridBackgroundProps {
  /** Approximate cell size in pixels. Grid recalculates columns/rows from container size. */
  cellSize?: number;
  /** Fraction of cells (0..1) that animate; rest are dark. */
  density?: number;
  /** Mask the edges so the grid fades into the section background. */
  fadeEdges?: boolean;
}

interface PixelCell {
  isLit: boolean;
  delay: number;
  duration: number;
  intensity: number;
}

const buildCells = (count: number, density: number): PixelCell[] =>
  Array.from({ length: count }, () => {
    const isLit = Math.random() < density;
    return {
      isLit,
      // Negative delay starts each cell mid-animation, so on first paint
      // they are scattered across the cycle instead of all peaking together.
      delay: -Math.random() * 10,
      duration: 5 + Math.random() * 6,
      // Keep peaks subtle so the field never overpowers the foreground.
      intensity: 0.04 + Math.random() * 0.1
    };
  });

/**
 * Decorative animated pixel-grid that sits behind hero/section content.
 * Cells are kept roughly square on any viewport by deriving columns/rows
 * from container size at runtime. Each lit cell has a randomised delay,
 * duration and peak opacity so the field feels organic rather than
 * uniformly pulsing. Pure CSS — no rAF.
 */
export const PixelGridBackground = ({
  cellSize = 44,
  density = 0.18,
  fadeEdges = true
}: PixelGridBackgroundProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ cols: 0, rows: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const resize = () => {
      const rect = el.getBoundingClientRect();
      const cols = Math.max(1, Math.floor(rect.width / cellSize));
      const rows = Math.max(1, Math.floor(rect.height / cellSize));
      setSize((prev) => (prev.cols === cols && prev.rows === rows ? prev : { cols, rows }));
    };

    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(el);

    return () => observer.disconnect();
  }, [cellSize]);

  const cells = useMemo(
    () => buildCells(size.cols * size.rows, density),
    [size.cols, size.rows, density]
  );

  const maskStyle = fadeEdges
    ? {
        maskImage: 'radial-gradient(ellipse at center, black 25%, transparent 80%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 25%, transparent 80%)'
      }
    : undefined;

  return (
    <div
      ref={ref}
      aria-hidden
      className="absolute inset-0 grid pointer-events-none"
      style={{
        gridTemplateColumns: `repeat(${size.cols}, 1fr)`,
        gridTemplateRows: `repeat(${size.rows}, 1fr)`,
        gap: '2px',
        ...maskStyle
      }}
    >
      {cells.map((cell, idx) =>
        cell.isLit ? (
          <span
            key={idx}
            className="bg-mantis animate-pixel-blink"
            style={{
              animationDelay: `${cell.delay}s`,
              animationDuration: `${cell.duration}s`,
              ['--pixel-peak' as string]: cell.intensity.toFixed(2)
            }}
          />
        ) : (
          <span key={idx} />
        )
      )}
    </div>
  );
};
